import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
} from "@mui/material";
import * as React from "react";
import { DimensionamentoService } from "../../services/DimensionamentoServices";
import Swal from "sweetalert2";
import { useNavigate } from "react-router-dom";
import RootCauseFormDialog from "../Forms/RootCauseFormDialog";
import TruncatedTableCell from "./TruncatedTableCell";
import { FaPlus } from "react-icons/fa";

const getEndDateStyle = (finalDate, status) => {
  if (!finalDate || status === "COMPLETED") {
    return {};
  }

  const currentDate = new Date();
  let endDate;

  if (typeof finalDate === "string") {
    endDate = new Date(finalDate);
  } else if (typeof finalDate === "number") {
    endDate = new Date(finalDate);
  } else {
    return {};
  }

  if (isNaN(endDate.getTime())) {
    return {};
  }

  const oneWeekBefore = new Date(endDate);
  oneWeekBefore.setDate(oneWeekBefore.getDate() - 7);

  currentDate.setHours(0, 0, 0, 0);
  endDate.setHours(0, 0, 0, 0);
  oneWeekBefore.setHours(0, 0, 0, 0);

  if (currentDate > endDate) {
    return {
      backgroundColor: "#fef2f2",
      color: "#dc2626",
      borderLeft: "3px solid #dc2626",
      fontWeight: 500,
    };
  } else if (currentDate >= oneWeekBefore && currentDate <= endDate) {
    return {
      backgroundColor: "#fff7ed",
      color: "#ea580c",
      borderLeft: "3px solid #ea580c",
      fontWeight: 500,
    };
  }

  return {};
};

const RootCauseTableReport = ({ data, causeAndActionData }) => {
  const navigate = useNavigate();
  const [formDialogOpen, setFormDialogOpen] = React.useState(false);

  const statusNames = [
    { name: "Não iniciado", value: "NOT_STARTED" },
    { name: "Em andamento", value: "IN_PROGRESS" },
    { name: "Concluído", value: "COMPLETED" },
    { name: "Cancelado", value: "CANCELLED" },
    { name: "Em espera", value: "ON_HOLD" },
    { name: "Em revisão", value: "UNDER_REVIEW" },
  ];

  const handleSaveRootCause = async (formData) => {
    const reportData = [
      {
        referenceCode: data.referenceCode,
        parameterName: data.parameterName,
        serviceType: data.serviceType,
        city: data.cityObj,
        description: data.networkSufficiency,
        cause: formData.cause,
        action: formData.action,
        responsible: formData.responsible,
        initialDate: formData.initialDate
          ? new Date(formData.initialDate).getTime()
          : null,
        finalDate: formData.finalDate
          ? new Date(formData.finalDate).getTime()
          : null,
        status: formData.status,
      },
    ];

    try {
      await DimensionamentoService.postCauseAndActionReport(reportData);

      await Swal.fire({
        icon: "success",
        title: "Sucesso!",
        text: "Análise de Causa Raiz/Plano de Ação salvos com sucesso.",
        confirmButtonText: "Ok",
      });

      navigate("/reports", {
        state: {
          selectedStep: {
            name: "Leitos Hospitalares",
            customName: "Gestão de Rede",
            rowData: undefined,
          },
        },
      });
    } catch (error) {
      console.error("Erro ao enviar dados:", error);
      await Swal.fire({
        icon: "error",
        title: "Erro!",
        text: "Ocorreu um erro ao salvar os dados. Tente novamente.",
        confirmButtonText: "Ok",
      });
    }
  };

  const formatDateToShow = (timestamp) => {
    if (!timestamp) return "";
    const date = new Date(timestamp);
    const year = date.getUTCFullYear();
    const month = String(date.getUTCMonth() + 1).padStart(2, "0");
    const day = String(date.getUTCDate()).padStart(2, "0");
    return `${day}/${month}/${year}`;
  };

  return (
    <div>
      <Box
        sx={{
          mb: 3,
          p: 3,
          backgroundColor: "#f8fafc",
          borderRadius: "12px",
          border: "1px solid #e2e8f0",
        }}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", md: "row" },
            justifyContent: "space-between",
            alignItems: { xs: "stretch", md: "flex-start" },
            gap: { xs: 2, md: 0 },
            mb: 2,
          }}
        >
          <div>
            <h3
              style={{
                margin: 0,
                marginBottom: "8px",
                color: "#1f2937",
                fontSize: "1.25rem",
              }}
            >
              Criar Análise de Causa Raiz/Plano de Ação
            </h3>
            <p style={{ margin: 0, color: "#6b7280", fontSize: "0.875rem" }}>
              Defina a causa raiz e o plano de ação para resolver a situação
              identificada
            </p>
          </div>
          <Button
            variant="contained"
            startIcon={<FaPlus />}
            onClick={() => setFormDialogOpen(true)}
            sx={{
              backgroundColor: "#059669",
              color: "white",
              textTransform: "none",
              borderRadius: "8px",
              fontSize: "0.875rem",
              fontWeight: 500,
              px: 3,
              py: 1.5,
              boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.1)",
              alignSelf: { xs: "flex-center", md: "flex-start" },
              minWidth: { xs: "auto", md: "auto" },
              "&:hover": {
                backgroundColor: "#047857",
                boxShadow: "0 6px 8px -1px rgba(0, 0, 0, 0.15)",
              },
            }}
          >
            Criar Plano de Ação
          </Button>
        </Box>
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: {
              xs: "1fr",
              sm: "1fr 1fr",
              md: "repeat(4, 1fr)",
            },
            gap: 2,
            mt: 2,
          }}
        >
          <Box>
            <span
              style={{ fontSize: "0.75rem", color: "#6b7280", fontWeight: 500 }}
            >
              CÓDIGO
            </span>
            <p
              style={{
                margin: 0,
                fontSize: "0.875rem",
                color: "#1f2937",
                fontWeight: 600,
              }}
            >
              {data.referenceCode}
            </p>
          </Box>
          <Box>
            <span
              style={{ fontSize: "0.75rem", color: "#6b7280", fontWeight: 500 }}
            >
              PARÂMETRO
            </span>
            <p
              style={{
                margin: 0,
                fontSize: "0.875rem",
                color: "#1f2937",
                fontWeight: 600,
              }}
            >
              {data.parameterName}
            </p>
          </Box>
          <Box>
            <span
              style={{ fontSize: "0.75rem", color: "#6b7280", fontWeight: 500 }}
            >
              TIPO PRESTADOR
            </span>
            <p
              style={{
                margin: 0,
                fontSize: "0.875rem",
                color: "#1f2937",
                fontWeight: 600,
              }}
            >
              {data.serviceType || "Prestador Desconhecido"}
            </p>
          </Box>
          <Box>
            <span
              style={{ fontSize: "0.75rem", color: "#6b7280", fontWeight: 500 }}
            >
              SITUAÇÃO
            </span>
            <p
              style={{
                margin: 0,
                fontSize: "0.875rem",
                color: "#1f2937",
                fontWeight: 600,
              }}
            >
              {data.networkSufficiency}
            </p>
          </Box>
        </Box>
      </Box>
      <RootCauseFormDialog
        open={formDialogOpen}
        onClose={() => setFormDialogOpen(false)}
        onSave={handleSaveRootCause}
        referenceData={{
          referenceCode: data.referenceCode,
          parameterName: data.parameterName,
          serviceType: data.serviceType,
          networkSufficiency: data.networkSufficiency,
          city: data.cityObj.city,
        }}
      />

      {causeAndActionData && causeAndActionData.length > 0 && (
        <>
          <div className="pt-8 pb-4">
            <h2 className="text-lg font-medium text-center text-gray-700">
              Dados já cadastrados ({causeAndActionData.length})
            </h2>
          </div>
          <div className="pt-4 w-full h-full space-y-6 pb-6">
            <TableContainer
              component={Paper}
              sx={{
                boxShadow:
                  "0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)",
                borderRadius: "8px",
                border: "1px solid #f0f0f0",
              }}
            >
              <Table>
                <TableHead>
                  <TableRow
                    sx={{
                      backgroundColor: "#fdfdfd",
                      borderBottom: "1px solid #f0f0f0",
                    }}
                  >
                    <TableCell
                      sx={{
                        fontWeight: 500,
                        fontSize: "0.875rem",
                        color: "#374151",
                        py: 1.5,
                        borderBottom: "1px solid #f0f0f0",
                      }}
                    >
                      Código
                    </TableCell>
                    <TableCell
                      sx={{
                        fontWeight: 500,
                        fontSize: "0.875rem",
                        color: "#374151",
                        py: 1.5,
                        borderBottom: "1px solid #f0f0f0",
                      }}
                    >
                      Parâmetro
                    </TableCell>
                    <TableCell
                      sx={{
                        fontWeight: 500,
                        fontSize: "0.875rem",
                        color: "#374151",
                        py: 1.5,
                        borderBottom: "1px solid #f0f0f0",
                      }}
                    >
                      Tipo Prestador
                    </TableCell>
                    <TableCell
                      sx={{
                        fontWeight: 500,
                        fontSize: "0.875rem",
                        color: "#374151",
                        py: 1.5,
                        borderBottom: "1px solid #f0f0f0",
                      }}
                    >
                      Descrição
                    </TableCell>
                    <TableCell
                      sx={{
                        fontWeight: 500,
                        fontSize: "0.875rem",
                        color: "#374151",
                        py: 1.5,
                        borderBottom: "1px solid #f0f0f0",
                      }}
                    >
                      Causa
                    </TableCell>
                    <TableCell
                      sx={{
                        fontWeight: 500,
                        fontSize: "0.875rem",
                        color: "#374151",
                        py: 1.5,
                        borderBottom: "1px solid #f0f0f0",
                      }}
                    >
                      Ação
                    </TableCell>
                    <TableCell
                      sx={{
                        fontWeight: 500,
                        fontSize: "0.875rem",
                        color: "#374151",
                        py: 1.5,
                        borderBottom: "1px solid #f0f0f0",
                      }}
                    >
                      Responsável
                    </TableCell>
                    <TableCell
                      sx={{
                        fontWeight: 500,
                        fontSize: "0.875rem",
                        color: "#374151",
                        py: 1.5,
                        borderBottom: "1px solid #f0f0f0",
                      }}
                    >
                      Data Início
                    </TableCell>
                    <TableCell
                      sx={{
                        fontWeight: 500,
                        fontSize: "0.875rem",
                        color: "#374151",
                        py: 1.5,
                        borderBottom: "1px solid #f0f0f0",
                      }}
                    >
                      Data Término
                    </TableCell>
                    <TableCell
                      sx={{
                        fontWeight: 500,
                        fontSize: "0.875rem",
                        color: "#374151",
                        py: 1.5,
                        borderBottom: "1px solid #f0f0f0",
                      }}
                    >
                      Estado
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {causeAndActionData.map((item, index) => (
                    <TableRow
                      key={index}
                      sx={{
                        "&:hover": { backgroundColor: "#fafafa" },
                        borderBottom:
                          index === causeAndActionData.length - 1
                            ? "none"
                            : "1px solid #f0f0f0",
                      }}
                    >
                      <TableCell
                        sx={{
                          fontSize: "0.875rem",
                          color: "#1f2937",
                          py: 2,
                          borderBottom: "none",
                        }}
                      >
                        {item.attribute.attribute.referenceCode}
                      </TableCell>
                      <TableCell
                        sx={{
                          fontSize: "0.875rem",
                          color: "#1f2937",
                          py: 2,
                          borderBottom: "none",
                        }}
                      >
                        {item.attribute.attribute.name}
                      </TableCell>
                      <TableCell
                        sx={{
                          fontSize: "0.875rem",
                          color: "#1f2937",
                          py: 2,
                          borderBottom: "none",
                        }}
                      >
                        {item.serviceType || "Prestador Desconhecido"}
                      </TableCell>
                      <TruncatedTableCell
                        content={item.description}
                        title="Descrição - Detalhes"
                        maxLength={80}
                        sx={{
                          minWidth: 125,
                          maxWidth: 200,
                          fontSize: "0.875rem",
                          color: "#1f2937",
                          py: 2,
                          borderBottom: "none",
                        }}
                      />
                      <TruncatedTableCell
                        content={item.cause}
                        title="Causa Raiz - Detalhes"
                        maxLength={100}
                        sx={{
                          minWidth: 200,
                          maxWidth: 300,
                          fontSize: "0.875rem",
                          color: "#1f2937",
                          py: 2,
                          borderBottom: "none",
                        }}
                      />
                      <TruncatedTableCell
                        content={item.action}
                        title="Plano de Ação - Detalhes"
                        maxLength={100}
                        sx={{
                          minWidth: 200,
                          maxWidth: 300,
                          fontSize: "0.875rem",
                          color: "#1f2937",
                          py: 2,
                          borderBottom: "none",
                        }}
                      />
                      <TruncatedTableCell
                        content={item.responsible}
                        title="Responsável - Detalhes"
                        maxLength={50}
                        sx={{
                          maxWidth: 200,
                          fontSize: "0.875rem",
                          color: "#1f2937",
                          py: 2,
                          borderBottom: "none",
                        }}
                      />
                      <TableCell
                        sx={{
                          fontSize: "0.875rem",
                          color: "#1f2937",
                          py: 2,
                          borderBottom: "none",
                        }}
                      >
                        {item.initialDate
                          ? formatDateToShow(item.initialDate)
                          : ""}
                      </TableCell>
                      <TableCell
                        sx={{
                          fontSize: "0.875rem",
                          color: "#1f2937",
                          py: 2,
                          borderBottom: "none",
                          ...getEndDateStyle(item.finalDate, item.status),
                        }}
                      >
                        {item.finalDate ? formatDateToShow(item.finalDate) : ""}
                      </TableCell>
                      <TableCell
                        sx={{
                          fontSize: "0.875rem",
                          py: 2,
                          borderBottom: "none",
                        }}
                      >
                        <span
                          className={`
                          inline-flex items-center px-2 py-1 rounded-md text-xs font-medium
                          ${
                            item.status === "COMPLETED"
                              ? "bg-green-50 text-green-700 border border-green-200"
                              : item.status === "IN_PROGRESS"
                              ? "bg-blue-50 text-blue-700 border border-blue-200"
                              : item.status === "NOT_STARTED"
                              ? "bg-gray-50 text-gray-700 border border-gray-200"
                              : item.status === "CANCELLED"
                              ? "bg-red-50 text-red-700 border border-red-200"
                              : item.status === "ON_HOLD"
                              ? "bg-yellow-50 text-yellow-700 border border-yellow-200"
                              : item.status === "UNDER_REVIEW"
                              ? "bg-purple-50 text-purple-700 border border-purple-200"
                              : "bg-gray-50 text-gray-700 border border-gray-200"
                          }
                        `}
                        >
                          {statusNames.find((s) => s.value === item.status)
                            ?.name ?? "Não definido"}
                        </span>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </div>
        </>
      )}
    </div>
  );
};

export default RootCauseTableReport;
